import type { ComponentType } from "react";
import type { CheckSpec } from "@howcomet/shared";
import { PickShell } from "./PickShell";
import { OrderShell } from "./OrderShell";
import { SortShell } from "./SortShell";
import { MatchShell } from "./MatchShell";

/**
 * The client half of the shell plugin system. The server owns validation and
 * generation; this owns rendering. They meet on the `type` string.
 *
 * Adding a shell here is one import and one entry. Everything else — the
 * frame's wrong/retry/complete guarantees, the award flow, the star — is
 * inherited.
 */

export interface CheckResult {
  correct: boolean;
  firstTry: boolean;
}

export interface ShellProps<Spec = CheckSpec> {
  spec: Spec;
  onDone: (result: CheckResult) => void;
  /** True while the award request is in flight, so the button can't double-fire. */
  collecting: boolean;
}

const RENDERERS: Record<string, ComponentType<ShellProps<never>>> = {
  pick: PickShell as ComponentType<ShellProps<never>>,
  order: OrderShell as ComponentType<ShellProps<never>>,
  sort: SortShell as ComponentType<ShellProps<never>>,
  match: MatchShell as ComponentType<ShellProps<never>>,
};

export function hasRenderer(type: string): boolean {
  return Object.hasOwn(RENDERERS, type);
}

/**
 * Renders a spec, or degrades honestly if it can't. An app installed before a
 * new shell shipped will land here rather than crashing — the child is told
 * plainly and still gets their star, because the alternative is a white screen
 * with a star trapped behind it.
 */
export function Check({ spec, onDone, collecting }: ShellProps) {
  const Renderer = RENDERERS[spec.type];

  if (!Renderer) {
    return (
      <div className="mt-5 pop rounded-3xl border-2 border-lilac bg-panel2 p-5">
        <p className="disp text-[20px] text-cream">This one needs the newest app.</p>
        <p className="mt-2 text-[16px] text-cream/70">
          Ask a grown-up to update Howcomet. Your star still counts.
        </p>
        <button
          onClick={() => onDone({ correct: true, firstTry: false })}
          className="kidbtn disp mt-4 w-full rounded-3xl bg-gold py-4 text-[21px] text-night shadow-[0_6px_0_rgba(0,0,0,.35)]"
        >
          Collect my star ⭐
        </button>
      </div>
    );
  }

  return <Renderer spec={spec as never} onDone={onDone} collecting={collecting} />;
}
